import React, { useState, useEffect } from 'react'
import { Alert, Button, Col, Form, Row } from 'react-bootstrap'
import './QuestionnaireStep.css'
import AnswerButton from './AnswerButton'
import QuestionRow from './QuestionRow'

const QUESTIONS = [
  {
    id: 'has_overdue_taxes',
    text: 'Does the company currently have any overdue taxes (Corporation Tax, PAYE or VAT)?',
  },
  {
    id: 'has_staff_involved_not_on_payroll',
    text: "Are any staff involved in R&D projects who are not on the company's payroll?",
  },
  {
    id: 'accounts_provided',
    text: 'Have accounts been provided to Ayming?',
    subField: {
      type: 'date',
      field: 'accounts_provided_date',
      label: 'Expected Date',
      showOn: 'no',
    },
  },
  {
    id: 'tax_computations_provided',
    text: 'Have tax computations been provided to Ayming?',
    subField: {
      type: 'date',
      field: 'tax_computations_provided_date',
      label: 'Expected Date',
      showOn: 'no',
    },
  },
  {
    id: 'development_costs_capitalised',
    text: 'Have any development costs been capitalised?',
  },
  {
    id: 'contracted_by_third_party',
    text: 'Has the company been contracted to carry out R&D by any third party?',
  },
  {
    id: 'received_grants_or_subsidies',
    text: "Has the company received any grants or subsidies towards its R&D activities?",
    subField: {
      type: 'text',
      field: 'received_grants_or_subsidies_details',
      label: 'Brief details',
      showOn: 'yes',
    },
  },
  {
    id: 'reimburses_staff_expenses',
    text: 'Does the company reimburse staff expenses?',
  },
  {
    id: 'purchased_company_cars',
    text: 'Does the company pay car allowance to those that have been involved in R&D?',
  },
  {
    id: 'has_patents',
    text: "Are any patents registered in the company's name?",
  },
  {
    id: 'is_business_registered_in_northern_ireland',
    text: 'Is the business registered in Northern Ireland?',
  },
]

const TOTAL = QUESTIONS.length

const initAnswersFromClaimGroup = (claimGroup) => {
  const answers = {}
  QUESTIONS.forEach(q => {
    const val = claimGroup[q.id]
    if (val === true) answers[q.id] = 'yes'
    else if (val === false && claimGroup.id) {
      answers[q.id] = 'no'
    }
  })
  return answers
}

export default function QuestionnaireStep({ claimGroup, onChange, onBack, onSubmit, loading, isEdit }) {
  const [answers, setAnswers] = useState(() => initAnswersFromClaimGroup(claimGroup))
  const [showValidation, setShowValidation] = useState(false)

  useEffect(() => {
    setAnswers(initAnswersFromClaimGroup(claimGroup))
  }, [claimGroup.id])

  const answeredCount = Object.keys(answers).filter(k => answers[k] !== 'skip').length
  const skippedCount = Object.values(answers).filter(a => a === 'skip').length
  const totalTouched = Object.keys(answers).length
  const unansweredIds = QUESTIONS.map(q => q.id).filter(id => !answers[id])

  const getStatus = () => {
    if (totalTouched === 0) return 'not_started'
    if (totalTouched === TOTAL && skippedCount === 0) return 'complete'
    return 'in_progress'
  }

  const status = getStatus()

  const setAnswer = (questionId, value) => {
    const updated = { ...answers, [questionId]: value }
    setAnswers(updated)

    if (value !== 'skip') {
      const boolValue = value === 'yes' ? true : false
      onChange({ target: { name: questionId, value: boolValue } })
    }

    if (showValidation && Object.keys(updated).length === TOTAL) {
      setShowValidation(false)
    }
  }

  const handleComplete = () => {
    if (isEdit) {
      onSubmit()
      return
    }
    if (unansweredIds.length > 0) {
      setShowValidation(true)
      return
    }
    onSubmit()
  }

  const statusLabel = {
    not_started: 'Not started',
    in_progress: 'In Progress',
    complete: 'Complete',
  }

  return (
    <div className="questionnaire-step">
      <h5 className="questionnaire-step__title">Questionnaire</h5>

      <div className="questionnaire-step__status-bar">
        <span className={`questionnaire-step__status-pill questionnaire-step__status-pill--${status}`}>
          {statusLabel[status]}
        </span>
        <span className="questionnaire-step__status-count">
          {answeredCount} of {TOTAL} answered
          {skippedCount > 0 && ` (${skippedCount} skipped)`}
        </span>
      </div>

      {showValidation && unansweredIds.length > 0 && (
        <Alert variant="danger" className="questionnaire-step__alert">
          Please answer all questions before completing setup. Unanswered questions are highlighted below.
        </Alert>
      )}

      <div className="questionnaire-step__question-list">
        {QUESTIONS.map((q, i) => {
          const answer = answers[q.id]
          const isUnanswered = showValidation && !answer

          return (
            <QuestionRow
              key={q.id}
              question={q}
              index={i}
              answer={answer}
              isUnanswered={isUnanswered}
              isEdit={isEdit}
              onAnswer={setAnswer}
              claimGroup={claimGroup}
              onSubFieldChange={onChange}
            />)
        })}
      </div>

      <div className="questionnaire-step__footer">
        <Button variant="primary" onClick={onBack} className="questionnaire-step__back-btn">
          ← Back
        </Button>
        <Button
          variant="success"
          onClick={handleComplete}
          disabled={loading}
          className="questionnaire-step__complete-btn"
        >
          {loading ? 'Saving...' : isEdit ? 'Save' : 'Complete Setup'}
        </Button>
      </div>
    </div>
  )
}
